import { buildSystemPrompt, buildClaudeUserContent } from "./prompts";

const OPENROUTER_API = "https://openrouter.ai/api/v1/chat/completions";
const API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;
const IMAGE_MODEL =
  process.env.REACT_APP_OPENROUTER_IMAGE_MODEL || "google/gemini-2.5-flash-image";

const STYLES = ["minimal", "vivid", "premium"];

const STYLE_LABELS = {
  minimal: "Минималистичный",
  vivid: "Яркий",
  premium: "Премиум",
};

function imageModalitiesForModel(model) {
  const m = String(model || "").toLowerCase();
  if (m.includes("flux") || m.includes("sourceful")) return ["image"];
  return ["image", "text"];
}

function pickImageUrlFromMessage(message) {
  const images = message?.images;
  if (!Array.isArray(images) || images.length === 0) return null;
  const first = images[0];
  return first?.image_url?.url || first?.imageUrl?.url || null;
}

async function generateImageDataUrl(prompt, { aspectRatio = "16:9", imageSize = "1K" } = {}) {
  const safePrompt = String(prompt || "").trim() || "Clean advertising display banner, professional, Russian text in design brief only.";
  const model = IMAGE_MODEL;
  const response = await fetch(OPENROUTER_API, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${API_KEY}`,
      "Content-Type": "application/json",
      "HTTP-Referer": typeof window !== "undefined" ? window.location.origin : "https://lk.programmatic.ru",
      "X-Title": "AI Креативы — Programmatic",
    },
    body: JSON.stringify({
      model,
      messages: [{ role: "user", content: safePrompt }],
      modalities: imageModalitiesForModel(model),
      image_config: {
        aspect_ratio: aspectRatio,
        image_size: imageSize,
      },
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Image generation error ${response.status}`);
  }

  const data = await response.json();
  const url = pickImageUrlFromMessage(data?.choices?.[0]?.message);
  return url || null;
}

function normalizePromptsData(parsed, raw, formData) {
  const base = `Бренд ${formData?.brandName || ""}. Оффер: ${formData?.offer || ""}. Заголовок: ${formData?.headline || ""}.`;
  const fallback = {
    minimal: `${base} Стиль: минимализм, много воздуха, типографика. Только русский текст на баннере. Рекламный баннер 1200×628.`,
    vivid: `${base} Стиль: яркий, контрастный, динамичная композиция. Только русский текст. Рекламный баннер 1200×628.`,
    premium: `${base} Стиль: тёмный премиум, золотые акценты, солидность. Только русский текст. Рекламный баннер 1200×628.`,
  };

  if (!parsed || typeof parsed !== "object") {
    const text = typeof raw === "string" ? raw : "";
    return {
      minimal: `${text} ${fallback.minimal}`,
      vivid: `${text} ${fallback.vivid}`,
      premium: `${text} ${fallback.premium}`,
    };
  }

  const p = parsed.prompts && typeof parsed.prompts === "object" ? parsed.prompts : parsed;
  const minimal = p.minimal ?? p.minimalist;
  const vivid = p.vivid;
  const premium = p.premium;

  if (minimal || vivid || premium) {
    return {
      minimal: String(minimal || fallback.minimal),
      vivid: String(vivid || fallback.vivid),
      premium: String(premium || fallback.premium),
    };
  }

  const text = typeof raw === "string" ? raw : JSON.stringify(parsed);
  return {
    minimal: `${text} ${fallback.minimal}`,
    vivid: `${text} ${fallback.vivid}`,
    premium: `${text} ${fallback.premium}`,
  };
}

async function callClaude(messages) {
  const response = await fetch(OPENROUTER_API, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${API_KEY}`,
      "Content-Type": "application/json",
      "HTTP-Referer": typeof window !== "undefined" ? window.location.origin : "https://lk.programmatic.ru",
      "X-Title": "AI Креативы — Programmatic",
    },
    body: JSON.stringify({
      model: "anthropic/claude-sonnet-4-5",
      messages,
      max_tokens: 1500,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || `OpenRouter error ${response.status}`);
  }

  const data = await response.json();
  return data.choices?.[0]?.message?.content || "";
}

export async function generateBanners(formData, onStep) {
  onStep("Анализируем бренд и формируем концепцию...");

  let promptsData;
  let concept = "";

  if (!API_KEY) {
    await new Promise((r) => setTimeout(r, 1200));
    promptsData = {
      minimal: `Clean minimalist advertisement banner for ${formData.brandName}, white background, bold typography, Russian text only`,
      vivid: `Bright vivid advertisement banner for ${formData.brandName}, high contrast colors, dynamic composition, Russian text only`,
      premium: `Premium elegant dark advertisement banner for ${formData.brandName}, luxury style, gold accents, Russian text only`,
    };
    const refs = formData.referenceImageDataUrls?.filter(Boolean)?.length || 0;
    concept = refs
      ? `Рекламная кампания для ${formData.brandName}: ${formData.offer} (ориентир — ${refs} референс-изображени${refs === 1 ? "е" : refs < 5 ? "я" : "й"})`
      : `Рекламная кампания для ${formData.brandName}: ${formData.offer}`;
  } else {
    const userContent = buildClaudeUserContent(formData);
    const raw = await callClaude([
      { role: "system", content: buildSystemPrompt() },
      { role: "user", content: userContent },
    ]);

    let parsed = null;
    try {
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      parsed = JSON.parse(jsonMatch?.[0] || raw);
    } catch {
      parsed = null;
    }

    promptsData = normalizePromptsData(parsed, raw, formData);
    concept =
      (parsed && typeof parsed === "object" && parsed.concept) ||
      `Рекламная кампания для ${formData.brandName}: ${formData.offer}`;
  }

  onStep("Генерируем варианты баннеров...");

  const variants = await Promise.all(
    STYLES.map(async (style) => {
      let imageUrl = null;

      if (API_KEY) {
        try {
          imageUrl = await generateImageDataUrl(promptsData[style], {
            aspectRatio: "16:9",
            imageSize: "1K",
          });
        } catch {
          imageUrl = null;
        }
      }

      return {
        id: style,
        style,
        label: STYLE_LABELS[style],
        imageUrl,
        prompt: promptsData[style],
      };
    })
  );

  return { variants, concept };
}

const RESIZE_ASPECT = {
  leaderboard: "21:9",
  rectangle: "5:4",
  halfpage: "9:16",
};

export async function generateResizes(selectedVariant, formData, onStep) {
  onStep("Генерируем ресайзы...");

  const basePrompt = String(selectedVariant?.prompt || "").trim() || `Ad banner for ${formData?.brandName || "brand"}. Russian text.`;

  const sizes = [
    { key: "leaderboard", label: "728×90 — Горизонтальный", width: 728, height: 90, size: "728x90" },
    { key: "rectangle", label: "300×250 — Прямоугольник", width: 300, height: 250, size: "300x250" },
    { key: "halfpage", label: "300×600 — Вертикальный", width: 300, height: 600, size: "300x600" },
  ];

  if (!API_KEY) {
    await new Promise((r) => setTimeout(r, 1000));
    return sizes.map((s) => ({ ...s, imageUrl: null }));
  }

  const results = await Promise.all(
    sizes.map(async (s) => {
      const ar = RESIZE_ASPECT[s.key] || "16:9";
      const prompt = `${basePrompt} Adapt layout to aspect ratio ${ar}, same brand and message, IAB display format ${s.size}.`;
      try {
        const imageUrl = await generateImageDataUrl(prompt, {
          aspectRatio: ar,
          imageSize: "1K",
        });
        return { ...s, imageUrl };
      } catch {
        return { ...s, imageUrl: null };
      }
    })
  );

  return results;
}
